import { Progress } from '@heroui/react';
import { useId, useRef, useState, type ChangeEvent, type DragEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../../api/client.js';
import { filesApi, type StoredFile } from '../../api/endpoints.js';
import { ActionButton } from './ActionButton.js';
import { Alert } from './feedback.js';
import { formatBytes } from '../format.js';
import { currentLocale } from '../../i18n/index.js';

/**
 * File upload and download.
 *
 * Two components, both built from the shared library rather than reaching
 * past it, and both holding to the honesty rules that apply here more sharply
 * than anywhere else in the product:
 *
 *  - **Uploading a file does not mean the system read or verified it**
 *    (CLAUDE.md section 8). `FileUploadField` says a file was ATTACHED. It
 *    never says it was checked, understood, or that its numbers were used.
 *  - **A success message reflects what actually happened.** The confirmation
 *    is raised from the server's response, never from the moment of the click.
 *  - The type and size hints shown here are a COURTESY. The real gate is the
 *    server, which inspects the actual bytes; a file that passes the check
 *    below can still be refused, and the refusal is shown as it arrives.
 *  - Nothing here builds a path. A file is addressed by id and downloaded
 *    through an endpoint.
 *
 * FILES THAT BELONG TO A RECORD WITH ITS OWN PERMISSION (Phase 8, D92) — a
 * sample photo, an attempt's worksheet — are uploaded and downloaded through
 * that record's route, never the generic one. Both components therefore take
 * an optional function that does the transfer, and fall back to the generic
 * files API only when none is given.
 */

// ---------------------------------------------------------------------------
// Upload
// ---------------------------------------------------------------------------

export interface FileUploadFieldProps<T = StoredFile> {
  /** The owner type for the GENERIC files API. Ignored when `upload` is given. */
  readonly entityType?: string;
  readonly entityId?: string;
  readonly label: string;
  readonly description?: string;
  /**
   * Performs the transfer through a record's own route. When given, the
   * generic files API is not used at all.
   */
  readonly upload?: (file: File) => Promise<T>;
  /** Raised with the SERVER's confirmation, never on the click. */
  readonly onUploaded: (result: T) => void;
  /** A courtesy hint for the picker. The server decides what is accepted. */
  readonly accept?: string;
  readonly maxBytes?: number;
  readonly isDisabled?: boolean;
}

export function FileUploadField<T = StoredFile>({
  entityType,
  entityId,
  label,
  description,
  upload,
  onUploaded,
  accept,
  maxBytes,
  isDisabled = false,
}: FileUploadFieldProps<T>) {
  const { t } = useTranslation();
  const locale = currentLocale();
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const asSize = (bytes: number) => formatBytes(bytes, { locale }) ?? '';

  const [isUploading, setUploading] = useState(false);
  const [isDragging, setDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const send = async (file: File) => {
    setError(null);

    // A local size check, purely so an obviously oversized file is not sent
    // over the wire only to be refused. It is NOT the rule; the server's is.
    if (maxBytes !== undefined && file.size > maxBytes) {
      setError(t('files.errorTooLarge', { size: asSize(maxBytes) }));
      return;
    }

    setUploading(true);
    try {
      const stored = upload
        ? await upload(file)
        : ((await filesApi.upload({
            file,
            entityType: entityType ?? '',
            ...(entityId ? { entityId } : {}),
          })) as unknown as T);
      // Raised from the SERVER's confirmation, not from the button press.
      onUploaded(stored);
    } catch (caught) {
      setError(messageFor(caught, t));
    } finally {
      setUploading(false);
      // Cleared so choosing the same file again still fires a change event.
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) void send(file);
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
    if (isDisabled || isUploading) return;
    const file = event.dataTransfer.files[0];
    if (file) void send(file);
  };

  return (
    <div className="flex flex-col gap-inline">
      <label htmlFor={inputId} className="text-body font-medium text-default-700">
        {label}
      </label>

      {/*
        A drop target that is ALSO a real button and a real file input, so it
        works with a keyboard and with a screen reader. A drag-only surface
        would be unreachable for anyone not using a mouse.
      */}
      <div
        onDrop={handleDrop}
        onDragOver={(event) => {
          event.preventDefault();
          if (!isDisabled && !isUploading) setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        className={[
          'flex flex-col items-center gap-inline rounded-medium border-2 border-dashed px-4 py-6 text-center transition-colors',
          isDragging
            ? 'border-primary-500 bg-primary-50'
            : 'border-default-300 bg-default-50',
          isDisabled ? 'opacity-60' : '',
        ].join(' ')}
      >
        <input
          ref={inputRef}
          id={inputId}
          type="file"
          className="sr-only"
          onChange={handleChange}
          disabled={isDisabled || isUploading}
          {...(accept ? { accept } : {})}
        />

        <ActionButton
          priority="secondary"
          size="sm"
          isLoading={isUploading}
          isDisabled={isDisabled}
          onPress={() => inputRef.current?.click()}
        >
          {t('files.chooseFile')}
        </ActionButton>

        <p className="text-caption text-default-600">
          {t('files.dropHint')}
        </p>

        {description ? (
          <p className="text-caption text-default-600">{description}</p>
        ) : null}

        {maxBytes !== undefined ? (
          <p className="text-caption text-default-600">
            {t('files.maxSize', { size: asSize(maxBytes) })}
          </p>
        ) : null}
      </div>

      {isUploading ? (
        <Progress
          isIndeterminate
          size="sm"
          aria-label={t('files.uploading')}
        />
      ) : null}

      {error ? <Alert tone="danger" title={error} /> : null}

      {/*
        Stated plainly, because the requirements name this exact confusion:
        attaching a file is not the system reading it, checking it, or using
        anything inside it.
      */}
      <p className="text-caption text-default-600">{t('files.notVerified')}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Download
// ---------------------------------------------------------------------------

export interface FileDownloadButtonProps {
  readonly fileId: string;
  readonly label?: string;
  readonly disposition?: 'attachment' | 'inline';
  readonly isDisabled?: boolean;
  /**
   * Fetches the bytes through a record's own authenticated route. When given,
   * no signed link is requested — the server would refuse one for such a file.
   */
  readonly fetchContent?: () => Promise<Blob>;
  /** The name to save under when `fetchContent` is used. */
  readonly fileName?: string;
}

export function FileDownloadButton({
  fileId,
  label,
  disposition = 'attachment',
  isDisabled = false,
  fetchContent,
  fileName,
}: FileDownloadButtonProps) {
  const { t } = useTranslation();
  const [isPreparing, setPreparing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const open = async () => {
    setError(null);
    setPreparing(true);
    try {
      if (fetchContent) {
        const blob = await fetchContent();
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.rel = 'noopener';
        if (disposition === 'attachment') anchor.download = fileName ?? fileId;
        else anchor.target = '_blank';
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        // Released after the browser has had the chance to start the save.
        window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
      } else {
        const link = await filesApi.link(fileId, disposition);
        window.open(link.url, '_blank', 'noopener,noreferrer');
      }
    } catch (caught) {
      setError(messageFor(caught, t));
    } finally {
      setPreparing(false);
    }
  };

  return (
    <div className="flex flex-col gap-inline">
      <ActionButton
        priority="tertiary"
        size="sm"
        isLoading={isPreparing}
        isDisabled={isDisabled}
        onPress={() => void open()}
      >
        {label ?? t('files.download')}
      </ActionButton>
      {error ? <Alert tone="danger" title={error} /> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared
// ---------------------------------------------------------------------------

function messageFor(
  caught: unknown,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  if (caught instanceof ApiError) {
    const known = [
      'FILE_TOO_LARGE',
      'UNSUPPORTED_FILE_TYPE',
      'FILE_CONTENT_MISMATCH',
      'STORAGE_QUOTA_EXCEEDED',
      'STORAGE_NOT_CONFIGURED',
      'PERMISSION_DENIED',
      'NOT_FOUND',
      'VALIDATION_FAILED',
      'RECORD_LOCKED',
      'INVALID_STATE_TRANSITION',
    ];
    if (known.includes(caught.code)) return t(`errors.${caught.code}`);
  }
  return t('errors.INTERNAL_ERROR');
}
